import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Providers } from '@/app/providers';
import { App } from '@/app/App';
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary';
import { reportClientError } from '@/shared/components/reportClientError';
import { installChunkReloadGuard } from '@/shared/chunkReloadGuard';
import { isAgGridFloatingFilterRace } from '@/shared/grid/agGridKnownRaces';
import '@/styles/globals.css';

// #2740: recover stale post-deploy tabs whose lazy chunks 404 (vite:preloadError → one
// guarded reload); AppErrorBoundary below backstops anything that still throws so the app
// never dies to a silent blank page.
installChunkReloadGuard();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root element');
}

// slice-3616: uncaught errors and rejected promises outside React's render tree also reach the
// server log (see reportClientError). Boundary-caught render errors report from the boundary.
window.addEventListener('error', (e) => {
  const message = e.message || String(e.error);
  const stack = (e.error as { stack?: string } | undefined)?.stack;
  // Crash lane 19 Oct: AG-Grid's harmless floating-filter teardown race is tagged, not a crash.
  const source = isAgGridFloatingFilterRace(message, stack) ? 'ag-grid-filter-race' : 'window.error';
  reportClientError({ message, stack, source });
});
window.addEventListener('unhandledrejection', (e) => {
  const r = e.reason as { message?: string; stack?: string } | undefined;
  reportClientError({ message: r?.message ?? String(e.reason), stack: r?.stack, source: 'unhandledrejection' });
});

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <Providers>
          <App />
        </Providers>
      </BrowserRouter>
    </AppErrorBoundary>
  </StrictMode>,
);
